// Pure helper module for monetary display on the dashboard.
//
// Owned by task 3.2 of the dashboard-redesign spec (Requirement 4 · P2).
// See `.kiro/specs/dashboard-redesign/design.md` §Correctness Properties · P2
// and `.kiro/steering/design-language.md` §"Semantic — money".
//
// Constitution Rule 1: all monetary values are stored as integer pence.
// Display via the canonical conversion `(pence || 0) / 100` with exactly
// two decimal places, prefixed with `£`. `null` / `undefined` / non-numeric
// inputs render `£0.00`. Negative values render a single leading minus
// (e.g. `-£12.50`).
//
// This module has NO React imports and NO side effects — it is a pure
// helper that Wave 3 dashboard widgets will consume alongside their
// hooks' `WidgetResult<T>` payloads.

/**
 * The semantic direction a monetary value points in.
 *
 * - `"owed"`  — a receivable (money owed **to** the tenant, e.g. arrears,
 *   collections, reserve-fund balance, cash at bank).
 * - `"owing"` — a payable  (money owing **by** the tenant, e.g. a bill in
 *   the activity feed).
 *
 * The colour returned by {@link getMonetaryColour} depends on both the sign
 * of the pence value and this context. See the function for the mapping.
 */
export type MonetaryContext = "owed" | "owing";

/**
 * The Owed semantic colour token (design-language.md — receivable).
 * Also mirrored in `.kiro/specs/dashboard-redesign/requirements.md` §4.4.
 */
export const COLOUR_OWED = "#16a34a";

/**
 * The Owing semantic colour token (design-language.md — payable).
 * Also mirrored in `.kiro/specs/dashboard-redesign/requirements.md` §4.5.
 */
export const COLOUR_OWING = "#dc2626";

/**
 * The neutral / muted colour used when a monetary value is exactly zero.
 *
 * design-language.md lists this as the "Muted" text token (`#6b7280`) and
 * uses it for secondary text and labels. Zero has no semantic direction —
 * nothing is owed and nothing is owing — so rendering it in the muted grey
 * is the least misleading choice on a treasurer's Day-1 dashboard.
 *
 * Note: design-language.md §"Semantic — money" also permits keeping the
 * semantic colour on a zero balance (so a `£0.00` in an Owed column stays
 * green). Task 3.2 explicitly requires the zero-is-neutral rule for the
 * dashboard, so this module is authoritative for the dashboard surface.
 */
export const COLOUR_NEUTRAL = "#6b7280";

/**
 * Format an integer-pence value as a GBP display string.
 *
 * Behaviour (mirrors Constitution Rule 1 and design-language.md
 * §"Monetary Display"):
 *
 * - `null` / `undefined` / non-numeric / non-finite → `£0.00`
 * - `0` → `£0.00`
 * - Positive `p` (integer pence) → `£{p / 100}.toFixed(2)`
 * - Negative `p` → `-£{|p| / 100}.toFixed(2)` (single leading minus)
 * - Non-integer pence uses the integer portion only (never more than two
 *   decimal places on the output). This preserves the invariant that the
 *   output regex `/^-?£\d{1,15}\.\d{2}$/` matches on every call.
 *
 * @param pence Integer minor units. Widgets receive this from
 *   `WidgetResult<T>` payloads whose money fields are integer pence.
 * @returns A GBP display string matching `/^-?£\d{1,15}\.\d{2}$/`.
 */
export function formatMoneyGBP(pence: number | null | undefined): string {
  // Null / undefined / non-numeric → "£0.00" (Requirement 4.2).
  if (pence === null || pence === undefined) {
    return "£0.00";
  }
  const asNumber = Number(pence);
  if (!Number.isFinite(asNumber)) {
    return "£0.00";
  }

  // Use the integer-pence portion only. Math.trunc keeps the sign but
  // discards the fractional part in either direction, so
  // formatMoneyGBP(5501.9) === "£55.01" and formatMoneyGBP(-1250.7) === "-£12.50".
  const integerPence = Math.trunc(asNumber);
  const absPence = Math.abs(integerPence);
  const pounds = absPence / 100;
  const formatted = pounds.toFixed(2);

  // `-£0.00` would be a bug — a truly zero value has no sign.
  if (integerPence < 0 && absPence !== 0) {
    return `-£${formatted}`;
  }
  return `£${formatted}`;
}

/**
 * Derive the semantic colour for a monetary display, given the widget's
 * `MonetaryContext` and the pence value it is about to render.
 *
 * Mapping:
 *
 * | pence | context   | colour                              |
 * | ----- | --------- | ----------------------------------- |
 * | `> 0` | `"owed"`  | {@link COLOUR_OWED}  (`#16a34a`)    |
 * | `> 0` | `"owing"` | {@link COLOUR_OWING} (`#dc2626`)    |
 * | `< 0` | `"owed"`  | {@link COLOUR_OWING} (semantic flip)|
 * | `< 0` | `"owing"` | {@link COLOUR_OWED}  (semantic flip)|
 * | `= 0` | either    | {@link COLOUR_NEUTRAL} (`#6b7280`)  |
 * | `null`/`undefined`/non-finite | either | {@link COLOUR_NEUTRAL} |
 *
 * The semantic flip on negatives keeps the colour tied to the *actual*
 * direction of money movement: a negative receivable is really a payable,
 * so it renders red; a negative payable is really a receivable, so it
 * renders green. Zero is neutral by construction (see {@link COLOUR_NEUTRAL}).
 *
 * @param pence   Integer minor units, or `null` / `undefined`.
 * @param context The widget's semantic direction — `"owed"` or `"owing"`.
 * @returns One of `#16a34a`, `#dc2626`, or `#6b7280`.
 */
export function getMonetaryColour(
  pence: number | null | undefined,
  context: MonetaryContext,
): typeof COLOUR_OWED | typeof COLOUR_OWING | typeof COLOUR_NEUTRAL {
  if (pence === null || pence === undefined) {
    return COLOUR_NEUTRAL;
  }
  const asNumber = Number(pence);
  if (!Number.isFinite(asNumber)) {
    return COLOUR_NEUTRAL;
  }
  const integerPence = Math.trunc(asNumber);

  if (integerPence === 0) {
    return COLOUR_NEUTRAL;
  }
  if (integerPence > 0) {
    return context === "owed" ? COLOUR_OWED : COLOUR_OWING;
  }
  // integerPence < 0 — semantic flip so the visual matches the direction
  // of the actual balance rather than the widget's declared context.
  return context === "owed" ? COLOUR_OWING : COLOUR_OWED;
}
